import { refreshAccessToken, type RefreshResult } from '@/hooks/actions/use-refresh-token-action';
import { redirect } from 'react-router-dom';
import { tokenManager } from './token-manager';

/**
 * Shared refresh promise to prevent concurrent refresh calls
 * across auth-guard, axios interceptor, and useTokenRefresh hook.
 */
let refreshPromise: Promise<RefreshResult> | null = null;

/**
 * Get or create a shared refresh promise.
 * Exported for use by the axios interceptor and useTokenRefresh hook
 * to prevent duplicate concurrent refresh calls.
 */
export function getSharedRefreshPromise(): Promise<RefreshResult> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      // Small delay before clearing to prevent re-triggers
      // from microtask-queued callers in the same tick
      setTimeout(() => {
        refreshPromise = null;
      }, 100);
    });
  }
  return refreshPromise;
}

/**
 * Ensures a valid access token is available before proceeding.
 * Designed to be called from React Router loaders (outside React component tree).
 *
 * - If token is valid: returns immediately
 * - If token is expired but refresh token exists: attempts refresh
 * - If no auth at all or refresh fails: throws redirect('/') for React Router
 * - If network error during refresh: throws Response(503) for RouteErrorBoundary
 */
export async function ensureValidToken(): Promise<void> {
  // getValidAccessToken returns null if expired
  if (tokenManager.getValidAccessToken()) {
    return;
  }

  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) {
    throw redirect('/');
  }

  const result = await getSharedRefreshPromise();

  if (result.success) {
    return;
  }

  if (result.reason === 'auth_failure' || result.reason === 'no_refresh_token') {
    throw redirect('/');
  }

  // Network/server errors — show error boundary with retry option
  throw new Response('Unable to authenticate. Please check your connection and try again.', {
    status: 503,
  });
}
